import React from 'react';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiDrawer, { DrawerProps as MuiDrawerProps } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { Typography } from '../Typography';
export interface DrawerProps {
  /**
   * The content of the drawer.
   */
  children: React.ReactNode;
  /**
   * On close callback function
   */
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * If true the Drawer will appear
   */
  open: boolean;
  /**
   * The title text to display at the top of the Drawer
   */
  title: string | JSX.Element;
  /**
   * The edge of the screen the Drawer is anchored to. Defaults to `'bottom'` (a bottom sheet); use `'right'` /
   * `'left'` for a full-height side panel.
   */
  anchor?: MuiDrawerProps['anchor'];
  /**
   * Optional actions rendered in the header to the left of the close button (e.g. a share button)
   */
  headerActions?: React.ReactNode;
  /**
   * Renders a divider beneath the header
   */
  headerDivider?: boolean;
  /**
   * Removes the default padding around the content, letting the caller manage a full-bleed layout (e.g.
   * full-width dividers between sections)
   */
  disableContentPadding?: boolean;
  /**
   * The close button's accessible label. Defaults to `'close'`.
   */
  closeLabel?: string;
  /**
   * Any additional styles to apply to the Drawer
   */
  sx?: MuiDrawerProps['sx'];
  /**
   * The data test attribute
   */
  dataTestId?: string;
  /**
   * Drawer PaperProps
   */
  PaperProps?: Partial<MuiDrawerProps['PaperProps']>;
}
/**
 * Drawer component used across the app for both bottom sheets (the default, e.g. the mobile version of the
 * Autocomplete component or a fixed set of options / text) and full-height side panels (`anchor="right"` /
 * `"left"`). The header renders the title, any `headerActions`, and a close button; the body renders the
 * children, padded by default or full-bleed when `disableContentPadding` is set.
 */
export const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  children,
  onClose,
  open,
  title,
  anchor = 'bottom',
  headerActions,
  headerDivider = false,
  disableContentPadding = false,
  closeLabel = 'close',
  sx,
  dataTestId,
  PaperProps,
}: DrawerProps) => {
  const isSide = anchor === 'left' || anchor === 'right';
  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 6,
          pt: isSide ? 6 : 8,
          pb: isSide ? 4 : 0,
        }}
      >
        <Typography variant="h3">{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {headerActions}
          <IconButton aria-label={closeLabel} onClick={() => onClose(false)} size="large">
            <Close sx={{ color: 'black' }} />
          </IconButton>
        </Box>
      </Box>
      {headerDivider && <Divider />}
      {disableContentPadding ? children : <Box sx={{ p: 6 }}>{children}</Box>}
    </>
  );
  return (
    <MuiDrawer
      open={open}
      onClose={() => onClose(false)}
      anchor={anchor}
      transitionDuration={400}
      sx={sx}
      data-testid={dataTestId}
      PaperProps={PaperProps}
    >
      {isSide ? <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{content}</Box> : content}
    </MuiDrawer>
  );
};
